import React from 'react';
import MarkDown from '@/components/Documents/docs/MarkDown';

const md = `
# 接入文档
## 1. 系统交互介绍

这套方案一共有两部分:

* 大搜车调用合作公司 API
  * 大搜车业务方通过本服务，调用合作公司 API。
* 合作公司异步回调大搜车
  * 合作公司通过调用本服务，异步返回消息给公司业务方。


## 2. 关于消息类型 (messageType)

>
> **messageType** 是一个通用的字段。用于标记消息的业务类型。
>
> 例如弹个车的风控消息，我们会把 **messageType** 标记为 **risk**。
>
> 具体 messageType 由大搜车业务负责开发同学决定。

## 3. <大搜车调用合作公司>

> **公司业务方通过开方平台调用合作公司**
>
> 请求传输数据格式为 JSON（Content-Type: application/json） POST。
>
> 具体业务数据由合作公司和公司业务方相互指定。
>
> **鉴权方式采用开放平台统一鉴权**

### 3.1 请求地址

请求地址由合作公司提供。

推荐按照一种 messageType 提供一个地址。

### **3.2 合作公司收到的请求格式:**

\`\`\`
{
  "appKey": "appKey",
  "timestamp": "timestamp",
  "sign": "sign",
  "type": "message_type",
  "data": "{\"field1\": \"value1\"}"
}
\`\`\`

> 问: 为什么需要加入\`type\` 这个字段？
>
> 答: 虽然大部分合作工作公司会对每个消息类型都提供单独一个 URL。但也存在某些公司只提供一个 URL。而需添加一个 type 字段来实现对消息类型的区分。

### **3.2 合作公司返回数据的要求:**

推荐格式:

\`\`\`
{
  "code": "0",
  "message": "success",
  "data": {
    "aa": "bb"
  }
}
\`\`\`

开放平台中间层会对合作公司返回的数据进行处理。

- 有且只有 \`code\` 字段为 \`"0"\` 时，才判定请求为成功。所以 \`code\` 字段是必不可少的。
- 如果请求失败，则对公司业务方来说会收到请求失败的回复。
- 如果请求成功，则会将整个数据都包裹会送给业务方。

业务方最后获得数据格式将是:

\`\`\`
{
  "success": true,
  "message": "success",
  "code": 200,
  "data": {
      "code": "0",
      "message": "success",
      "data": {
      }
  }
}
\`\`\`

## 4. <合作公司异步回调大搜车>

### 4.1 接口形式

请求方式为 HTTP POST。

请求传输数据格式为 JSON（Content-Type: application/json）。

具体业务数据由合作公司和公司业务方相互指定。

签名格式采用开放平台统一签名方式。

异步接口回调通知，由大搜车提供 HTTP 接口，由合作公司调用。

### 4.2 请求的接口地址

\`\`\`
\${http://domain.com}/v1/callback/common/\${messageType}
\`\`\`

#### 4.2.1 服务地址

> appKey & appSecret 分为:
>
> 1. 测试环境
> 2. 预发环境 & 生产环境
>
> 如项目上线请及时向对接业务方索要 appKey & appSecret。

| 环境     | 请求地址                               |
| -------- | -------------------------------------- |
| 线上环境 | https://izayoi-coffee.souche.com       |
| 预发环境 | http://izayoi-coffee.prepub.souche.com |
| 测试环境 | http://common.proxy.dasouche.com       |

#### 4.2.2 messageType

消息类型。见 [链接](https://souche.yuque.com/koumakan/izayoi-coffee-usage/zy2ku4#240e94f0)

### **4.3 合作公司异步回调格式和公司业务方最后获得的格式:**

有且只支持 JSON 形式，并直接把这个 JSON 字符串打包到 消息队列，中间除了验签不做额外处理。

例如外部合作公司请求回调 API。要求格式如下:

\`\`\`json
{
  "appKey": "appKey",
  "timestamp": "timestamp",
  "sign": "sign",
  "data": "{\"field\":\"value\"}"
}
\`\`\`

最后业务方在 ons 获得消息格式为:

\`\`\`json
{
  "type": "risk",
  "companyCode": "companyCode",
  "timestamp": "timestamp",
  "appKey": "appKey",
  "data": {
      "field": "value"    
  }
}
\`\`\`

## 5. 关于请求签名和验签方式

### 5.1 账户相关参数

| 参数名称  | 参数说明                                                   |
| --------- | ---------------------------------------------------------- |
| appKey    | app key。开放平台 app 唯一账户标识。                       |
| appSecret | app secret。开放平台参与鉴权的密钥。由大搜车开放平台提供。 |

### 5.2 请求主要参数

#### 5.1.1 公共参数

| 参数名称  | 参数类型 | 含义                                                         | 示例         |
| --------- | -------- | ------------------------------------------------------------ | ------------ |
| appKey    | String   | 合作公司标识，由大搜车提供                                   |              |
| timestamp | number   | 时间戳。单位为毫秒。时间戳不能超过当前时间前后五分钟。       | \`1528420183\` |
| sign      | string   | 签名。签名流程见[签名流程](https://souche.yuque.com/koumakan/izayoi-coffee-usage/zy2ku4#da849542) |              |

#### 5.1.2 接口业务参数

> 业务参数由公司业务方指定，但是要求参数必须为 number 或者 string（且不能与公共参数冲突）。
>
> 如果参数为  JSON 对象，需要先要将对象序列化成字符串。

例如参数:

\`\`\`
{
  ...
  "data": {
    "aa": "aa"
  }
  ...
}
\`\`\`

需要将 data 参数进行序列化最后得到

\`\`\`
{
  ...
  "data": "{\"aa\":\"aa\"}"
  ...
}
\`\`\`

最后再参与签名。

### 5.2 签名流程

> 1. 请求体中的参数除 sign 字段外，全部参与鉴权。
> 2. 所有参数按照字典序排序，按照\` key1=value1&key2=value2\` 的格式拼接成字符串（字符串最后不需要添加\`&\`）。
> 3. 将拼接后的字符串进行 base64 编码。
> 4. 将 base64 编码后的字符串前拼接上 \`\${appSecret}:\`。将拼接后的字符串进行 sha1 加密得到 sign 值。
> 5. 将 sign 作为参数写在请求体的 body 上(POST请求) 或者 query 上(GET 请求)。

#### 5.2.1 签名示例

请求数据：

\`\`\`
{
  "api": "com.souche.shield.service#getUserById",
  "appKey": "ad33d1190ff8383b5650097c0b6403868a82bd4c",
  "timestamp": 1528420183,
  "data": "{\"id\":\"xv1fg4h\"}",
  "sign": "9b94171630348afdb950b8c26edeec608e0c1f78"
}
\`\`\`

请求体参数排序并拼接字符串

\`\`\`
api=com.souche.shield.service#getUserById&appKey=ad33d1190ff8383b5650097c0b6403868a82bd4c&data={"id":"xv1fg4h"}&timestamp=1528420183
\`\`\`

将拼接后的字符串进行 base64 编码

\`\`\`
YXBpPWNvbS5zb3VjaGUuc2hpZWxkLnNlcnZpY2UjZ2V0VXNlckJ5SWQmYXBwS2V5PWFkMzNkMTE5MGZmODM4M2I1NjUwMDk3YzBiNjQwMzg2OGE4MmJkNGMmZGF0YT17ImlkIjoieHYxZmc0aCJ9JnRpbWVzdGFtcD0xNTI4NDIwMTgz
\`\`\`

将 base64 编码后的字符串前拼接上 \`\${appSecret}:\`.

\`\`\`
569f4f8050a4bfc87ebfaf1e0b4d71aae58f3958:YXBpPWNvbS5zb3VjaGUuc2hpZWxkLnNlcnZpY2UjZ2V0VXNlckJ5SWQmYXBwS2V5PWFkMzNkMTE5MGZmODM4M2I1NjUwMDk3YzBiNjQwMzg2OGE4MmJkNGMmZGF0YT17ImlkIjoieHYxZmc0aCJ9JnRpbWVzdGFtcD0xNTI4NDIwMTgz
\`\`\`

将拼接后的字符串进行 sha1 加密的到 sign 值

\`\`\`
9b94171630348afdb950b8c26edeec608e0c1f78
\`\`\`
`;

export default function () {
  return (
    <MarkDown docs={md}></MarkDown>
  )
}
